/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import ScheduleLiveConsultation from '@brands/Dashboard/Dashboard/DashboardModals/ScheduleLiveConsultation';
import { convertDateFormat } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { SelectedAppointment } from '@brands/Dashboard/PatientForms/PatientForm/utils/types';
import ProviderProfileCardModal from '@brands/Dashboard/ProfileCards/Provider/ProfileCards';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { rescheduleCase, RescheduleCasePayload } from '@brands/services/cases/rescheduleCase';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { getPricings } from '@brands/services/identity/getPricings';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { getUserById } from '@brands/services/identity/getUserById';
import { Insurance, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { selectLinkedCase } from '@brands/store/selectors/linkedCase';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setDependentId, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { setOrganizationPrices } from '@brands/store/slices/organizationPricingSlice';
import { setForm, setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { checkExternalFormAnswers } from '@brands/Utils/checkExternalForms';
import { displayErrorDetails, getRescheduleCasePaymentErrorCode } from '@brands/Utils/displayError';
import { getStaffReviewProfileBasePath } from '@brands/Utils/reviewProfileBasePath';
import { Button } from '@mui/material';
import Moment from 'moment';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MultiValue } from 'react-select';
import { toast } from 'react-toastify';

import Container from '../../../../Components/Container/Container';
import useAvailableProvidersNew from '../../../../hooks/useAvailableProvidersNew';
import useDebounce from '../../../../hooks/useDebounce';
import { useAppSelector } from '../../../../hooks/useReduxHook';
import useWindowSize from '../../../../hooks/useWindowSize';
import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import { IListAvailableProviderParams } from '../../../../services/identity/listAvailableProvidersDate';
import { shouldShowDoctorTitle } from '../../../../Utils/getProviderTitle';
import { Option, OptionLanguage } from '../../../../Utils/selectOptions';
import StateModal from '@brands/Dashboard/Dashboard/DashboardModals/StateModal/StateModal';
import ReschedulePaymentModal from '../utils/ReschedulePaymentModal/ReschedulePaymentModal';
import { useAvailabilityPatientId } from '../utils/useAvailabilityPatientId';
import { usePatientLocationStatePrompt } from '../utils/usePatientLocationStatePrompt';
import styles from './styles.module.scss';
import LanguageMultiSelect from './utils/LanguageMultiSelect';
import { formatTime } from './utils/utils';

const CLINICAL_CASES = [
  CaseType.video_call_scheduled,
  CaseType.phone_call_scheduled,
  CaseType.one_report,
  CaseType.second_opinion,
] as const;

const SelectProvider = (): JSX.Element => {
  const { state } = useLocation();
  const [backPath] = useState(() => {
    const lastPath = sessionStorage.getItem('lastNonSelectProviderPagePath') || '/';
    const forwardPaths = [
      '/clinical-data',
      '/payment-information',
      '/confirm-payment',
      '/select-pharmacy',
      '/describe-pain-level',
    ];
    if (!forwardPaths.some((p) => lastPath.includes(p))) {
      sessionStorage.setItem('selectProviderFrozenBackPath', lastPath);
    }
    return sessionStorage.getItem('selectProviderFrozenBackPath') || lastPath;
  });
  const { userInfo } = useAppSelector(selectAuth);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const selectedPatientId = selectedPatient?.value;
  const organizationPrices = useAppSelector(selectOrganizationPrices);
  // Captured once on mount, not recomputed every render: location.state can go
  // missing partway through this page's life (e.g. a re-render triggered
  // elsewhere in the tree that supplies no state for the current history
  // entry), which previously caused `onSubmit` to fall through to the new-case
  // branch mid-reschedule and send the patient through clinical-data intake
  // instead of straight to `attemptReschedule`. Freezing at mount makes the
  // reschedule context immune to that later loss.
  const [rescheduleContext] = useState(() => {
    const rawRescheduleCaseId = state?.caseId || searchParams.get('caseId');
    return {
      RescheduleCase: state?.RescheduleCase ? state.RescheduleCase : searchParams.get('reschedule') || null,
      rescheduleCaseId: rawRescheduleCaseId ? Number(rawRescheduleCaseId) : null,
      rescheduleCaseType: state?.caseType || searchParams.get('caseType'),
      RescheduleCaseSelectedCarrierId: state?.RescheduleCaseSelectedCarrierId || null,
      patient_current_state: state?.patient_current_state
        ? state?.patient_current_state
        : searchParams.get('case_state') || null,
    };
  });
  const {
    RescheduleCase,
    rescheduleCaseId,
    rescheduleCaseType,
    RescheduleCaseSelectedCarrierId,
    patient_current_state,
  } = rescheduleContext;
  // `RescheduleCase` alone is fragile — it only lives in location.state or a
  // query param, either of which can go missing (e.g. a reload). `rescheduleCaseId`
  // is the stronger signal: if it resolved to a real id, this is a reschedule
  // regardless of whether the `RescheduleCase` flag also survived.
  const isReschedule = Boolean(RescheduleCase) || Boolean(rescheduleCaseId);
  const { linkedCaseId } = useAppSelector(selectLinkedCase);
  // A program follow-up the provider kept for themselves ("Assign to me")
  // already has its provider before it is first scheduled, so the patient
  // only picks one of that provider's times. undefined while the linked
  // case is still loading, null when the patient chooses any provider.
  const [assignedProviderId, setAssignedProviderId] = useState<number | null | undefined>(() =>
    linkedCaseId && !isReschedule ? undefined : null,
  );
  const isProviderAssigned = typeof assignedProviderId === 'number';
  const selectedInsuranceId = (values?.find((item: any) => item.form_question_id === 19)?.value
    .dataWithUpdatedInsurance as Insurance)
    ? (values?.find((item: any) => item.form_question_id === 19)?.value.dataWithUpdatedInsurance as Insurance)
        .insurance_membership?.insurance_carrier_id
    : '';
  const screenSize = useWindowSize();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [languageFilter, setLanguageFilter] = useState<MultiValue<Option>>(() => {
    const saved =
      (values.find((item) => item.form_question_id === 22)?.value
        ?.selectedLanguagesForProvider as MultiValue<Option>) || [];
    return saved.length > 0 ? saved : [{ label: 'English', value: 'en', id: 1 }];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState<boolean>(false);
  const [providerParticipant, setProviderParticipant] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<SelectedAppointment>({
    selectedDate: '',
    selectedTimeSlot: '',
    selectedDateTime: '',
    selectedProviderId: undefined,
  });
  const [rescheduleSuccessModal, setRescheduleSuccessModal] = useState<{
    openModal: boolean;
    updatedCase: ICase | null;
  }>({
    openModal: false,
    updatedCase: null,
  });
  // Only shown reactively: rescheduleCase is always attempted first, and this
  // opens only if the backend reports the linked case still needs payment.
  // A genuine reschedule of an already-paid appointment never hits this.
  const [paymentRequiredModal, setPaymentRequiredModal] = useState(false);
  const [pendingReschedulePayload, setPendingReschedulePayload] = useState<RescheduleCasePayload | null>(null);
  const observer = useRef<IntersectionObserver>();
  const lastProviderItemRef = useRef<HTMLTableRowElement>(null);
  const currentState: Record<number, string> = useAppSelector(selectCurrentPatientState);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isAdmin =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin;

  /** Collector / CA create cases for another user — use that patient’s state for licensing, not the signed-in user. */
  const isStaffPickingForPatient =
    isAdmin || userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor;

  const licenseState = useMemo(() => {
    if (isReschedule && patient_current_state) {
      return patient_current_state;
    }
    const currentPatientId = currentPatient?.id ?? Number(selectedPatientId);
    if (isStaffPickingForPatient) {
      return currentPatientId ? currentState[currentPatientId] || '' : '';
    }
    // Not yet confirmed: never assume a real state for provider licensing —
    // an empty license_states filter is a safe "no match yet" (matches the
    // isStaffPickingForPatient branch above), not a silent guess.
    return currentState[userInfo.id] || '';
  }, [
    isReschedule,
    patient_current_state,
    isStaffPickingForPatient,
    currentState,
    currentPatient?.id,
    selectedPatientId,
    userInfo?.id,
  ]);

  const carrierId = useMemo(() => {
    if (selectedInsuranceId !== '' && selectedInsuranceId !== undefined) {
      return selectedInsuranceId;
    }
    if (isReschedule && RescheduleCaseSelectedCarrierId) {
      return RescheduleCaseSelectedCarrierId;
    }
    return undefined;
  }, [selectedInsuranceId, isReschedule, RescheduleCaseSelectedCarrierId]);

  const caseType = useMemo(() => {
    return (
      (isReschedule
        ? rescheduleCaseType
        : (values.find((item) => item.form_question_id === 20)?.value.caseType as string)) ||
      CaseType.video_call_scheduled
    );
  }, [isReschedule, rescheduleCaseType, values]);

  const availabilityPatientId = useAvailabilityPatientId(rescheduleCaseId);

  // An email-link reschedule lands here without passing the dashboard or
  // SelectPatient, so nothing has asked the patient where they are yet and the
  // license filter below would go out empty, matching no provider.
  const { isStateModalOpen, closeStateModal } = usePatientLocationStatePrompt(!licenseState);

  useEffect(() => {
    if (!linkedCaseId || isReschedule) return undefined;
    let canceled = false;
    getOneCase(linkedCaseId)
      .then((linkedCase) => {
        if (canceled) return;
        const providerId = linkedCase.provider_id ?? linkedCase.provider?.id;
        setAssignedProviderId(providerId && !linkedCase.video_conference_date ? providerId : null);
      })
      .catch(() => {
        // Falls back to the full list; scheduling the case with anyone but
        // its assigned provider is still refused by the backend.
        if (!canceled) setAssignedProviderId(null);
      });
    return (): void => {
      canceled = true;
    };
  }, [linkedCaseId, isReschedule]);

  const params = useMemo<IListAvailableProviderParams>(
    () => ({
      // An empty date holds the provider list back until we know whether
      // it has to be narrowed to the follow-up's assigned provider.
      date: assignedProviderId === undefined ? '' : moment().format('YYYY-MM-DD'),
      tz: timezone,
      license_states: [licenseState],
      synchronous: true,
      // This screen has no date picker of its own and only ever asks
      // about today; without a lookahead, a provider off just today
      // (e.g. a weekend) never appears here even with plenty of
      // availability later this week. Gated to this brand specifically
      // because @brands falls back to this file (before `default`) for
      // every brand with no SelectProvider override of its own — an
      // unguarded change here would silently reach all of them too.
      ...(process.env.REACT_APP_APP_BRAND === 'dentistry-one' ? { lookahead_days: 13 } : {}),
      ...(carrierId ? { carrier_id: carrierId } : {}),
      out_of_network: true,
      ...(availabilityPatientId ? { patient_id: availabilityPatientId } : {}),
      // The assigned provider is the only choice, so the language filter
      // (hidden for this case) must not hide them.
      ...(typeof assignedProviderId === 'number'
        ? { provider_id: assignedProviderId }
        : languageFilter.length > 0
        ? { language: languageFilter.map((item) => item.label) }
        : {}),
      ...(caseType === CaseType.oral_health_coaching ? { case_type: CaseType.oral_health_coaching } : {}),
    }),
    [timezone, licenseState, carrierId, availabilityPatientId, languageFilter, caseType, assignedProviderId],
  );

  const debouncedParams = useDebounce(params, 500);

  const { providers, loadNextPage, dataLoading, refreshProviders } = useAvailableProvidersNew(
    caseType === CaseType.oral_health_coaching ? 'care_advisors' : 'providers',
    debouncedParams,
  );
  // The hook skips a dateless query without loading, which would otherwise
  // flash "no providers" while the assigned provider is still resolving.
  const providersLoading = dataLoading || debouncedParams.date === '';

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().then(() => {
      setIsLoading(false);
    });
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const lastItem = entries[0];
    if (lastItem.isIntersecting && !isLoading) {
      loadMore();
    }
  };

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(handleIntersection);
    if (lastProviderItemRef.current) observer.current.observe(lastProviderItemRef.current);
  }, [providers]);

  const sortedProvidersWithTimeSlots = useMemo(() => {
    let nextAvailableSet = false;
    let nearestStartTimeIndex = -1;
    let nearestStartTimeDiff = Infinity;

    // Defensive: the backend already drops providers whose only same-day
    // slots are in the near past, but a stale cache / pagination quirk
    // could still hand us a provider with no bookable slots. Filter them
    // out so the renderer never has to short-circuit into a half-empty
    // card (name + photo + languages + blank slot area).
    const bookableProviders = providers.filter((p) => p.sync_time_slots.length > 0);

    // Step 1: Sort providers by the number of `sync_time_slots` (from most to least)
    const sortedProviders = [...bookableProviders].sort((a, b) => b.sync_time_slots.length - a.sync_time_slots.length);

    // Step 2: Find the provider with the nearest future time slot
    sortedProviders.forEach((provider, index) => {
      if (provider.sync_time_slots.length > 0) {
        const firstSlotStartTime = provider.sync_time_slots[0]; // Earliest slot of the provider
        const startTimeDiff = moment(firstSlotStartTime).diff(moment());

        if (startTimeDiff < nearestStartTimeDiff && startTimeDiff > 0) {
          nearestStartTimeIndex = index;
          nearestStartTimeDiff = startTimeDiff;
        }
      }
    });

    // Step 3: Move the provider with the nearest future time slot to the front
    if (nearestStartTimeIndex !== -1) {
      const nearestStartTimeProvider = sortedProviders.splice(nearestStartTimeIndex, 1)[0];
      sortedProviders.unshift(nearestStartTimeProvider);
    }

    // Step 4: Add `nextAvailable` to the first time slot of the first provider in the sorted list
    return sortedProviders.map((provider, index) => {
      const isNextAvailableProvider = index === 0 && !nextAvailableSet;

      if (isNextAvailableProvider) {
        nextAvailableSet = true;
      }

      const modifiedTimeSlots = provider.sync_time_intervals.map((slot, slotIndex) => ({
        ...slot,
        nextAvailable: isNextAvailableProvider && slotIndex === 0, // Mark only the first slot as `nextAvailable`
      }));

      return {
        ...provider,
        timeSlots: modifiedTimeSlots,
        allSlots: provider.sync_time_slots, // Retain all original time slots
      };
    });
  }, [providers]);

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    const transformedData = data.map((language) => ({
      value: language.locale,
      label: language.name,
      id: language.id,
    }));
    setLanguageOptions(transformedData);
  };

  useEffect(() => {
    (async (): Promise<void> => {
      await fetchLanguageOptions();

      const getPricingsFn = async (): Promise<void> => {
        try {
          const pricings = await getPricings();
          dispatch(setOrganizationPrices({ currentOrganizationPrices: pricings }));
        } catch (error: unknown) {
          displayErrorDetails(error);
        }
      };

      // Staff land here from linked-case notifications too, and the backend
      // refuses /pricings to anyone but a Patient ([12600-2]).
      if (userInfo.role.name === UserRoleName.Patient && !organizationPrices?.currentOrganizationPrices) {
        await getPricingsFn();
      }
    })();
  }, []);

  const collectorBackPath = useMemo(() => {
    if (userInfo.role.name !== UserRoleName.Collector) return null;
    const patientId = currentPatient?.id ?? Number(selectedPatient?.value);
    if (!patientId) return null;
    const roleSegment = currentPatient?.role?.name || UserRoleName.Patient;
    return `${getStaffReviewProfileBasePath(
      UserRoleName.Collector,
    )}/${roleSegment}/${patientId}/create-consultation-for`;
  }, [userInfo.role.name, currentPatient?.id, currentPatient?.role?.name, selectedPatient?.value]);

  /** The linked case's actual patient — needed if a staff-driven reschedule has to create a payment profile on their behalf. */
  const paymentProfileOwnerId =
    currentPatient?.id ?? (selectedPatientId ? Number(selectedPatientId) : undefined) ?? availabilityPatientId;

  const attemptReschedule = async (payload: RescheduleCasePayload): Promise<void> => {
    try {
      setLoading(true);
      const updatedCase = await rescheduleCase(rescheduleCaseId as number, payload);
      setPaymentRequiredModal(false);
      setPendingReschedulePayload(null);
      setRescheduleSuccessModal({
        openModal: true,
        updatedCase: updatedCase as ICase,
      });
    } catch (error: unknown) {
      const paymentErrorCode = getRescheduleCasePaymentErrorCode(error);
      if (paymentErrorCode === 'payment_profile_required') {
        // Strip any payment_profile_id so a later retry always starts from
        // the base payload before layering the freshly collected one on top.
        setPendingReschedulePayload({
          video_conference_date: payload.video_conference_date,
          clinician_id: payload.clinician_id,
          case_type: payload.case_type,
        });
        setPaymentRequiredModal(true);
      } else if (
        paymentErrorCode === 'invalid_payment_profile' ||
        paymentErrorCode === 'payment_authorization_failed'
      ) {
        displayErrorDetails(error);
        setPendingReschedulePayload({
          video_conference_date: payload.video_conference_date,
          clinician_id: payload.clinician_id,
          case_type: payload.case_type,
        });
        setPaymentRequiredModal(true);
      } else {
        displayErrorDetails(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentProfileCreated = async (paymentProfileId: number): Promise<void> => {
    if (!pendingReschedulePayload) return;
    await attemptReschedule({ ...pendingReschedulePayload, payment_profile_id: paymentProfileId });
  };

  const onSubmit = async (): Promise<void> => {
    if (selectedTimeSlot.selectedTimeSlot) {
      if (isReschedule && rescheduleCaseId) {
        // This page is reachable straight from an email link
        // (?reschedule=true&caseId=...) without ever going through
        // RescheduleButton.tsx's own checkExternalFormAnswers gate, so the
        // check has to be re-run here too rather than trusted to have
        // already happened upstream. Medical history is only mandatory for
        // Phone/Video Call consultations (matches ClinicalDataStepEight/
        // Nine's own CLINICAL_CASES scoping) - Oral Health Coaching never
        // collects it. Fetches the case directly instead of trusting
        // `caseType`/`currentPatient`, since all of those can be absent,
        // stale, or client-supplied-and-wrong for this same email-link
        // entry point - gating on the `caseType` prop instead of the
        // fetched case's own type would let a stale/crafted link skip the
        // check entirely.
        try {
          setLoading(true);
          const rescheduleTargetCase = await getOneCase(rescheduleCaseId);
          const isClinicalConsultation =
            rescheduleTargetCase.type === CaseType.video_call_scheduled ||
            rescheduleTargetCase.type === CaseType.phone_call_scheduled;
          // Staff may skip it for a patient who was never invited (for a
          // dependent, whose guardian was never invited): the client handles
          // that patient's care in-house. The API applies the same exemption.
          let patientNotInvited = false;
          if (isClinicalConsultation && isStaffPickingForPatient) {
            const { patient } = rescheduleTargetCase;
            const invitationHolder =
              patient.guardian_id != null
                ? await getUserById(
                    Number(patient.guardian_id),
                    userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor
                      ? userInfo.role.name
                      : undefined,
                  )
                : patient;
            patientNotInvited = invitationHolder?.status === 'Not Invited';
          }
          const requiresMedicalHistory = isClinicalConsultation && !patientNotInvited;
          if (requiresMedicalHistory) {
            const [profileComplete, medicalHistoryComplete, medicalConditionsComplete] = await checkExternalFormAnswers(
              userInfo,
              rescheduleTargetCase.patient as UserProfile,
            );
            // Staff book on the patient's behalf: /my-profile would open their
            // own profile, and an uninvited patient has no email by design, so
            // only the patient themself is sent to complete the profile.
            const missingProfile = !profileComplete && !isStaffPickingForPatient;
            if (missingProfile || !medicalHistoryComplete || !medicalConditionsComplete) {
              setLoading(false);
              dispatch(setIsFromSelectPatient(false));
              dispatch(setDependentId(null));
              if (missingProfile) {
                roleNavigate('/my-profile');
              } else {
                dispatch(
                  setSelectedPatient({
                    value: rescheduleTargetCase.patient.id.toString(),
                    label: rescheduleTargetCase.patient.full_name,
                  }),
                );
                // For staff, /medical-history edits the patient held here
                // before the one above, which may be whoever they last
                // worked on.
                if (isStaffPickingForPatient) {
                  dispatch(setSelectedPatientByAdmin({ selectedPatient: rescheduleTargetCase.patient }));
                }
                roleNavigate('/medical-history');
              }
              return;
            }
          }
        } catch (error: unknown) {
          setLoading(false);
          displayErrorDetails(error);
          return;
        }
        const payload: RescheduleCasePayload = {
          video_conference_date: convertDateFormat(selectedTimeSlot.selectedDateTime as string),
          clinician_id: selectedTimeSlot.selectedProviderId,
          ...(caseType === CaseType.oral_health_coaching ? { case_type: caseType } : {}),
        };
        await attemptReschedule(payload);
      } else {
        const filteredPayload = values.filter((item) => item.form_question_id !== 14 && item.form_question_id !== 22);

        filteredPayload.push(
          {
            form_question_id: 14,
            value: {
              selectedTimeSlot: selectedTimeSlot.selectedDateTime as string,
              ...(caseType !== CaseType.oral_health_coaching && {
                selectedProviderId: selectedTimeSlot.selectedProviderId,
              }),
              ...(caseType === CaseType.oral_health_coaching && {
                selectedCareAdvisorId: selectedTimeSlot.selectedProviderId,
              }),
            },
          },
          {
            form_question_id: 22,
            value: {
              selectedLanguagesForProvider: languageFilter,
            },
          },
        );
        dispatch(setForm(filteredPayload));
        if (userInfo.role.name === UserRoleName.Admin) {
          if (caseType === CaseType.care_coordination) {
            roleNavigate('/payment-information');
          } else {
            const needsPharmacy = caseType && CLINICAL_CASES.includes(caseType);
            if (needsPharmacy) {
              roleNavigate('/select-pharmacy');
            } else {
              roleNavigate('/describe-pain-level');
            }
          }
        } else {
          roleNavigate('/clinical-data', {
            state: {
              caseType: 'video_call_scheduled',
              patientId: selectedPatient?.value,
            },
          });
        }
      }
    } else {
      roleNavigate('/select-provider-details', {
        state: {
          caseType,
          patientId: selectedPatient?.value,
          provider_id: selectedTimeSlot.selectedProviderId,
          RescheduleCase: isReschedule,
          caseId: rescheduleCaseId,
        },
      });
    }
  };

  const handleTimeSlotClick = (date: string, slot: string, provider_id: number): void => {
    const currentTime = Moment(); // Get the current time
    const startTimeMoment = Moment(slot);
    const isCurrentDate = startTimeMoment.isSame(moment(), 'day');

    if (isCurrentDate) {
      const isPast = startTimeMoment.isBefore(currentTime);
      if (isPast) {
        toast.error('The time slot you selected is in the past. Please select a different time slot.', {
          className: 'fs-unmask',
        });
        return;
      }
    }

    if ((startTimeMoment.diff(currentTime, 'minutes') >= 30 && isCurrentDate) || !isCurrentDate) {
      const formattedForDisplay = startTimeMoment.format('dddd, MMMM D, YYYY h:mm a'); // 👈 For storing in Redux
      const formattedForSubmission = formatTime(startTimeMoment.toDate());
      setSelectedTimeSlot({
        selectedDate: date,
        selectedTimeSlot: formattedForSubmission,
        selectedDateTime: formattedForDisplay,
        selectedProviderId: provider_id,
      });
    } else {
      toast.error('The time slot you selected is no longer available. Please select a different time slot.', {
        className: 'fs-unmask',
      });
      refreshProviders();
      setSelectedTimeSlot({
        selectedDate: '',
        selectedTimeSlot: '',
        selectedDateTime: '',
        selectedProviderId: undefined,
      });
    }
  };

  useEffect(() => {
    const selectedSlotFormItem = values.find((item) => item.form_question_id === 14);

    const value = selectedSlotFormItem?.value;

    if (value && typeof value.selectedTimeSlot === 'string' && typeof value.selectedProviderId === 'number') {
      const storedDateTime = value.selectedTimeSlot;
      const providerId = value.selectedProviderId;

      const dateTimeMoment = moment(storedDateTime, 'dddd, MMMM D, YYYY h:mm a');
      const formattedTime = formatTime(dateTimeMoment.toDate());
      const formattedDate = dateTimeMoment.format('YYYY-MM-DD');

      setSelectedTimeSlot({
        selectedDate: formattedDate,
        selectedTimeSlot: formattedTime,
        selectedDateTime: storedDateTime,
        selectedProviderId: providerId,
      });
    }
  }, [values]);

  if (loading && !paymentRequiredModal) {
    return <Loading dotted />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: '24px',
        paddingBottom: '0px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.providerSelectContainer}`}>
        <PatientIntakeNavigation progress={8} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.providerSelectWrapper}>
            <div className={styles.providerSelectTitleContainer}>
              <div className={styles.providerSelectTitle} data-testid="select-provider-title">
                Select a provider and time{screenSize.width >= 768 ? '' : ' slot'}
              </div>
              {screenSize.width >= 768 && (
                <div className={styles.providerSelectSubtitle} data-testid="select-provider-time-zone">
                  Times are in {timezone}
                </div>
              )}
            </div>
            {!isProviderAssigned && (
              <div className={styles.providerSelectFilter}>
                <div className={styles.filterGroup}>
                  <LanguageMultiSelect
                    data-testid="select-provider-select-language"
                    selectedLanguages={languageFilter}
                    setSelectedLanguages={setLanguageFilter}
                    languageOptions={languageOptions}
                  />
                </div>
              </div>
            )}

            <div className={styles.providerSelectList}>
              {providersLoading ? (
                <div className={styles.loadingMore}>
                  <span>Loading more providers...</span>
                </div>
              ) : sortedProvidersWithTimeSlots.length > 0 ? (
                sortedProvidersWithTimeSlots.map((provider, index) => (
                  <div
                    key={provider.id}
                    ref={index === sortedProvidersWithTimeSlots.length - 1 ? lastProviderItemRef : null}
                  >
                    <div className={styles.providerSelectListItem}>
                      <div className={styles.providerSelectListItemDetails}>
                        <div
                          className={styles.imageContainer}
                          onClick={() => {
                            setProviderParticipant(provider.user);
                            setProviderProfileCardModal(true);
                          }}
                        >
                          <img
                            alt="Avatar"
                            className={`fs-exclude ${styles.providerImage}`}
                            src={getUserAvatar(provider?.id || 0)}
                            onError={(e) => {
                              e.currentTarget.src = '/assets/images/default-avatar.svg';
                            }}
                          />
                        </div>

                        <div className={styles.providerSelectListItemDetailsTextContainer}>
                          <div className={styles.providerSelectListItemDetailsText}>
                            <div
                              className={`fs-exclude ${styles.providerName}`}
                              onClick={() => {
                                setProviderParticipant(provider.user);
                                setProviderProfileCardModal(true);
                              }}
                              data-testid="select-provider-name"
                            >
                              {shouldShowDoctorTitle(provider.user?.provider_profile?.credentials) ? 'Dr. ' : ''}
                              {provider.user.full_name},{' '}
                              {provider.user?.provider_profile?.credentials || 'Unknown Title'}
                            </div>

                            <div className={styles.providerLanguages}>
                              <span className={styles.providerLanguagesLabel}>Languages: </span>
                              <span className={styles.providerLanguagesList}>
                                {provider.user?.basic_info?.languages?.length
                                  ? provider.user.basic_info.languages.map((lang) => lang?.name).join(', ')
                                  : 'No languages available'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={styles.providerSelectListItemTimeSlots}>
                        {(() => {
                          const firstSlot = provider.allSlots?.[0];
                          if (!firstSlot) return null;

                          const slotMoment = Moment(firstSlot);
                          const today = Moment().startOf('day');
                          const tomorrow = Moment().add(1, 'day').startOf('day');

                          const isToday = slotMoment.isSame(today, 'day');
                          const isTomorrow = slotMoment.isSame(tomorrow, 'day');

                          const formattedDateForState = slotMoment.format('YYYY-MM-DD');

                          const formattedDate = slotMoment.format('dddd, MMM D'); // e.g. Saturday, Nov 23

                          return (
                            <>
                              <div
                                className={styles.providerSelectListItemTimeSlotsLabel}
                                data-testid="select-provider-date"
                              >
                                {isToday && <span style={{ fontWeight: '700' }}>Today </span>}
                                {isTomorrow && !isToday && <span style={{ fontWeight: '700' }}>Tomorrow </span>}
                                <span>{formattedDate}</span>
                              </div>

                              <div
                                className={styles.providerSelectListItemTimeSlotContainer}
                                data-testid="select-provider-select-buttons"
                              >
                                {(() => {
                                  const firstSlotDay = slotMoment.startOf('day');
                                  const sameDaySlots = provider.allSlots.filter((slot) =>
                                    Moment(slot).startOf('day').isSame(firstSlotDay),
                                  );
                                  const displaySlots = sameDaySlots.slice(0, 3);
                                  const isNextAvailable = provider.timeSlots?.some((t) => t.nextAvailable);

                                  return displaySlots.map((slot, i) => {
                                    const matching = provider.timeSlots.find((t) => t.start_time === slot);
                                    const nextAvailable = isNextAvailable && matching?.nextAvailable;

                                    return (
                                      <Button
                                        key={i}
                                        onClick={() =>
                                          handleTimeSlotClick(firstSlotDay.format('YYYY-MM-DD'), slot, provider.id)
                                        }
                                        color="primary"
                                        variant="outlined"
                                        data-testid={`select-provider-date-button-${i}`}
                                        className={`${styles.providerSelectListItemTimeSlot} 
                                        
                                        ${nextAvailable ? styles.nextAvailable : ''} 
                                        ${
                                          selectedTimeSlot.selectedTimeSlot === formatTime(Moment(slot).toDate()) &&
                                          selectedTimeSlot.selectedProviderId === provider.id
                                            ? styles.selectedTimeSlot
                                            : ''
                                        }`}
                                      >
                                        {formatTime(Moment(slot).toDate())}
                                      </Button>
                                    );
                                  });
                                })()}
                              </div>

                              <div
                                className={styles.providerSelectListItemTimeSlotsMore}
                                onClick={() => {
                                  const existingValue = values.find((item) => item.form_question_id === 14)?.value;
                                  dispatch(
                                    setForm([
                                      ...values.filter((item) => item.form_question_id !== 14),
                                      {
                                        form_question_id: 14,
                                        value: {
                                          ...existingValue,
                                          selectedProvider: provider,
                                        },
                                      },
                                    ]),
                                  );
                                  roleNavigate('/select-provider-details', {
                                    state: {
                                      provider_details: provider,
                                      date: formattedDateForState,
                                      RescheduleCase: isReschedule,
                                      caseId: rescheduleCaseId,
                                    },
                                  });
                                }}
                              >
                                <span data-testid="select-provider-select-all-availability">See all availability</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noProviders}>
                  <div className={styles.noProvidersText}>Sorry, we currently don’t have providers.</div>
                </div>
              )}
              {isLoading && (
                <div className={styles.loadingMore}>
                  <span>Loading more...</span>
                </div>
              )}
            </div>
            {sortedProvidersWithTimeSlots.length === 0 && !providersLoading && (
              <div className={styles.noProviders}>
                <span className={styles.noProvidersText}>No providers available for the selected filters</span>
              </div>
            )}

            <div className={styles.providerSelectFooter}>
              <button
                type="button"
                data-testid="select-provider-button-back"
                className={styles.providerSelectFooterBackButton}
                onClick={() => roleNavigate(collectorBackPath || backPath)}
              >
                <FaChevronLeft />
                Back
              </button>
              <button
                type="submit"
                className={styles.providerSelectFooterNextButton}
                data-testid="select-provider-button-next"
                onClick={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
                disabled={
                  (caseType === CaseType.oral_health_coaching && !selectedTimeSlot.selectedProviderId) ||
                  (!selectedTimeSlot.selectedDate && !selectedTimeSlot.selectedTimeSlot)
                }
              >
                {isReschedule ? 'Reschedule' : 'Next'}
              </button>
            </div>
          </div>
        </form>
        {providerProfileCardModal && (
          <ProviderProfileCardModal
            isOpen={providerProfileCardModal}
            toggleModal={setProviderProfileCardModal}
            providerIdentity={providerParticipant}
            patientCurrentStateValue={licenseState}
          />
        )}
        {isStateModalOpen && <StateModal isOpen={isStateModalOpen} onClose={closeStateModal} />}
        {rescheduleSuccessModal.openModal && (
          <ScheduleLiveConsultation newCase={rescheduleSuccessModal.updatedCase as ICase} isRescheduled />
        )}
        {paymentRequiredModal && (
          <ReschedulePaymentModal
            isOpen={paymentRequiredModal}
            paymentProfileOwnerId={paymentProfileOwnerId}
            onClose={() => {
              setPaymentRequiredModal(false);
              setPendingReschedulePayload(null);
            }}
            onPaymentProfileCreated={handlePaymentProfileCreated}
          />
        )}
      </div>
    </Container>
  );
};
export default SelectProvider;
